import {
  ADMISSION_CHECK_CREATE,
  ADMISSION_CHECK_UPDATE,
  ADMISSION_CHECK_DELETE,
  ADMISSION_CHECKLIST_LOCATION,
  ADMISSION_USE_CACHE,
  ADMISSION_UPDATE_CONTENT,
  ADMISSION_UPDATE_CATEGORY,
  ADMISSION_CREATE_CATEGORY,
  ADMISSION_DELETE_CATEGORY,
  ADMISSION_CATEGORY_ORDER,
  ADMISSION_CHECKLIST_UPDATE_COMMENT,
  ADMISSION_CHECKLIST_UPDATE_CHECK,
  ADMISSION_CHECKLIST_RESET,
  ADMISSION_CHECK_ORDER,
  ADMISSION_UPDATE_CATEGORY_LOCATION,
} from '.';
import admissionServiceApi from '../../api/admission.service.api';
import labServiceApi from '../../api/lab.service.api';
import analyticsService from 'src/helpers/analytics.service';
import { ENTITY_TYPES, EVENTS } from '../../constants';
import { isConnected } from '../selectors/network.selector';

export const getAdmissionData = () => async (dispatch, getState) => {
  let state = getState();

  if (!isConnected(state)) {
    return dispatch({
      type: ADMISSION_USE_CACHE,
    });
  }

  let { data, error } = await admissionServiceApi.getAll();

  if (error) {
    return dispatch({
      type: ADMISSION_USE_CACHE,
    });
  }

  let { categories = [] } = data || {};

  let categoriesById = {};

  categories.forEach(category => (categoriesById[category.id] = category));

  dispatch({
    type: ADMISSION_UPDATE_CONTENT,
    payload: {
      categories,
      categoriesById,
    },
  });
};

export const moveAdmissionCategory =
  ({ id, categoryId, sourceId }) =>
  async dispatch => {
    let res = await admissionServiceApi.moveAdmissionCategory({
      id,
      categoryId,
      sourceId,
    });

    if (res.error) {
      throw new Error(res.data.message);
    }

    analyticsService.track(EVENTS.CATEGORIES.MOVE, {
      id,
      sourceId,
      targetId: categoryId,
      type: ENTITY_TYPES.admission,
    });

    dispatch({
      type: ADMISSION_UPDATE_CATEGORY_LOCATION,
    });
  };

export const updateCategory =
  ({ id, title, ownerIds, path, tags, keywords }) =>
  async dispatch => {
    let change = { id };

    if (title) change.title = title;
    if (ownerIds) change.ownerIds = ownerIds;
    if (path) change.path = path;
    if (tags) change.tags = tags;
    if (keywords) change.keywords = keywords;

    let res = await admissionServiceApi.updateCategory(change);

    if (res.error) {
      return { error: res.data.message };
    }
    // analyticsService.track(EVENTS.CONTENT.UPDATE, {
    //   entityType: ENTITY_TYPES.admission,
    // });
    dispatch({
      type: ADMISSION_UPDATE_CATEGORY,
      payload: { id, title, ownerIds, tags, keywords },
    });

    return { id, title, ownerIds, tags, keywords, type: res.data?.type };
  };

export const deleteCategory =
  ({ id }) =>
  async dispatch => {
    let res = await admissionServiceApi.deleteCategory({ id });

    if (res.error) {
      // TODO: add monitor
      return false;
    }

    dispatch({
      type: ADMISSION_DELETE_CATEGORY,
      payload: { id },
    });
  };

export const createCategory =
  ({ path, title, type, ownerIds, tags, keywords }) =>
  async (dispatch, getState) => {
    const workspaceId = getState().workspace.selected;

    let { error, data } = await admissionServiceApi.createCategory({
      data: {
        path,
        title,
        type,
        ownerIds,
        tags,
        keywords,
      },
      workspaceId,
    });

    if (error) {
      return { error: data.message };
    }

    let { category } = data;

    dispatch({
      type: ADMISSION_CREATE_CATEGORY,
      payload: {
        category,
        path,
      },
    });

    return category;
  };

export const updateCategorySubcategories =
  ({ id, categoryId, index }) =>
  async dispatch => {
    let res = await admissionServiceApi.updateCategorySubcategories({
      id,
      categoryId,
      index,
    });

    if (res.error) {
      // TODO: add monitor
      return false;
    }

    dispatch({
      type: ADMISSION_CATEGORY_ORDER,
      payload: { id, categoryId, index },
    });
  };

export const createCheck = data => async dispatch => {
  let res = await admissionServiceApi.createCheck(data);

  if (res.error) {
    throw new Error(res.data.message);
  }

  let { category } = res.data;

  dispatch({
    type: ADMISSION_CHECK_CREATE,
    payload: category,
  });
};

export const createQuickCheckListItem =
  ({ categoryId, tags }) =>
  async dispatch => {
    return dispatch(createCheck({ item: { title: '', tags }, categoryId }));
  };

export const updateCheck = data => async dispatch => {
  let res = await admissionServiceApi.updateCheck(data);

  if (res.error) {
    throw new Error(res.data.message);
  }

  let { category } = res.data;

  dispatch({
    type: ADMISSION_CHECK_UPDATE,
    payload: category,
  });
};

export const updateChecksOrder =
  ({ id, itemId, index }) =>
  async dispatch => {
    let res = await admissionServiceApi.updateChecksOrder({
      id,
      itemId,
      index,
    });

    if (res.error) {
      throw new Error(res.data.message);
    }

    dispatch({
      type: ADMISSION_CHECK_ORDER,
      payload: { id, itemId, index },
    });
  };

export const deleteCheck =
  ({ categoryId, id }) =>
  async dispatch => {
    let res = await admissionServiceApi.deleteCheck({ categoryId, id });

    if (res.error) {
      throw new Error(res.data.message);
    }

    let { category } = res.data;

    dispatch({
      type: ADMISSION_CHECK_DELETE,
      payload: category,
    });
  };

export const updateValueOrder =
  ({ id, itemId, index }) =>
  async dispatch => {
    let res = await labServiceApi.updateValueOrder({ id, itemId, index });

    if (res.error) {
      throw new Error(res.data.message);
    }

    dispatch({
      type: '', // LAB_UPDATE_ITEMS_ORDER,
      payload: { id, itemId, index },
    });
  };

export const moveChecklistAction =
  ({ id, categoryId, sourceId }) =>
  async dispatch => {
    let res = await admissionServiceApi.moveChecklist({
      id,
      categoryId,
      sourceId,
    });

    if (res.error) {
      throw new Error(res.data.message);
    }

    analyticsService.track(EVENTS.CHECKLISTS.MOVE, {
      id,
      sourceId,
      targetId: categoryId,
    });

    dispatch({
      type: ADMISSION_CHECKLIST_LOCATION,
      payload: { id, categoryId, sourceId },
    });
  };

export const updateChecklistComment = ({ comment, categoryId, id }) => ({
  type: ADMISSION_CHECKLIST_UPDATE_COMMENT,
  payload: { comment, categoryId, id },
});

export const updateChecklistState = ({ checked, categoryId, id }) => ({
  type: ADMISSION_CHECKLIST_UPDATE_CHECK,
  payload: { checked, categoryId, id },
});

export const resetChecklist = () => ({
  type: ADMISSION_CHECKLIST_RESET,
  payload: {},
});

export const generateChecklistReport =
  ({ sendByEmail = false, signature = {}, recipients = [] }) =>
  async (dispatch, getState) => {
    let { activeChecklist } = getState().admission;

    let res = await admissionServiceApi.generateReport({
      activeChecklist,
      sendByEmail,
      signature,
      recipients,
    });

    if (res.error) {
      throw new Error(res.data.message);
    }

    return res.data;
  };
